import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import BrazilMapModern from '../sales/BrazilMapModern';
import StatCard from '../common/StatCard';
import afterpayReportsService from '../../services/api/afterpayReports';
import { Button, Form, InputGroup, Card, ListGroup, Badge } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AfterPayReportsLayer = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('30');
  const [regionFilter, setRegionFilter] = useState('all');

  // Estados para dados dos relatórios (dados reais)
  const [stats, setStats] = useState({
    salesToday: 0,
    salesWeek: 0,
    salesMonth: 0,
    deliveryRate: 0,
    totalOrders: 0,
    totalRevenue: 0,
    averageTicket: 0
  });
  
  const [mapData, setMapData] = useState([]);
  const [monthlyEvolution, setMonthlyEvolution] = useState({
    labels: [],
    delivered: [],
    frustrated: []
  });
  const [ordersStatus, setOrdersStatus] = useState([]);

  // Formatadores
  const formatCurrency = (valueInCents) => {
    return afterpayReportsService.formatCurrency(valueInCents);
  };

  const formatNumber = (value) => {
    return afterpayReportsService.formatNumber(value);
  };

  // Carregar dados reais do backend
  const loadReportsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Carregando dados dos relatórios AfterPay...');
      console.log('🔄 Filtros aplicados:', { dateFilter, regionFilter });
      
      const filters = { dateFilter, regionFilter };
      
      // Buscar dados em paralelo para melhor performance
      const [
        statsData,
        mapDataResponse,
        monthlyEvolutionData,
        ordersStatusData
      ] = await Promise.all([
        afterpayReportsService.getMainStats(filters),
        afterpayReportsService.getMapData(filters),
        afterpayReportsService.getMonthlyEvolution(),
        afterpayReportsService.getOrdersStatus(filters)
      ]);
      
      // Atualizar estados com dados reais
      setStats(statsData);
      setMapData(mapDataResponse);
      setMonthlyEvolution(monthlyEvolutionData);
      setOrdersStatus(ordersStatusData);
      
      console.log('✅ Dados carregados com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      console.error('❌ Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      let errorMessage = 'Erro ao carregar dados. Tente novamente.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Erro de autenticação. Faça login novamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Sem permissão para acessar os relatórios.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Erro de conexão. Verifique se o backend está rodando.';
      }
      
      setError(errorMessage);
      
      // Manter dados vazios em caso de erro
      setStats({
        salesToday: 0,
        salesWeek: 0,
        salesMonth: 0,
        deliveryRate: 0,
        totalOrders: 0,
        totalRevenue: 0,
        averageTicket: 0
      });
      setMapData([]);
      setMonthlyEvolution({ labels: [], delivered: [], frustrated: [] });
      setOrdersStatus([]);
      
    } finally {
      setLoading(false);
    }
  };

  // useEffect para carregar dados quando o componente monta ou filtros mudam
  useEffect(() => {
    loadReportsData();
  }, [dateFilter, regionFilter]);

  // Dados dos gráficos baseados nos dados reais - SEMPRE mostrar gráfico
  const lineChartData = useMemo(() => {
    // Se não há dados, mostrar estrutura vazia mas válida
    const labels = monthlyEvolution.labels?.length > 0 ? monthlyEvolution.labels : ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
    const delivered = monthlyEvolution.delivered?.length > 0 ? monthlyEvolution.delivered : [0, 0, 0, 0, 0, 0];
    const frustrated = monthlyEvolution.frustrated?.length > 0 ? monthlyEvolution.frustrated : [0, 0, 0, 0, 0, 0];
    
    return {
      labels,
      datasets: [
        {
          label: 'Entregues',
          data: delivered,
          borderColor: '#198754',
          backgroundColor: 'rgba(25, 135, 84, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Frustrados',
          data: frustrated,
          borderColor: '#dc3545',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [monthlyEvolution]);

  const donutChartData = useMemo(() => {
    // Se não há dados, mostrar estrutura vazia mas válida
    if (!ordersStatus || ordersStatus.length === 0) {
      return {
        labels: ['Sem dados'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['#e9ecef'],
            borderWidth: 2,
            borderColor: '#ffffff',
            hoverOffset: 4,
          },
        ],
      };
    }

    const labels = ordersStatus.map(s => s.status);
    const data = ordersStatus.map(s => s.count);
    const backgroundColors = ordersStatus.map(s => s.color);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 4,
        },
      ],
    };
  }, [ordersStatus]);

  const top5States = useMemo(() => {
    return mapData.slice(0, 5);
  }, [mapData]);

  const barChartData = useMemo(() => {
    // Se não há dados, mostrar estrutura vazia mas válida
    if (!top5States || top5States.length === 0) {
      return {
        labels: ['Sem dados'],
        datasets: [
          {
            label: 'Pedidos',
            data: [0],
            backgroundColor: ['#e9ecef'],
            borderColor: ['#e9ecef'],
            borderWidth: 1,
          },
        ],
      };
    }

    const labels = top5States.map(s => s.region);
    const data = top5States.map(s => s.orders);
    const backgroundColors = ['#0d6efd', '#6610f2', '#198754', '#fd7e14', '#6c757d'];

    return {
      labels,
      datasets: [
        {
          label: 'Pedidos',
          data,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors,
          borderWidth: 1,
        },
      ],
    };
  }, [top5States]);

  const handleExportExcel = () => {
    alert('Funcionalidade de exportação para Excel será implementada em breve!');
  };

  const handleRegionClick = (regionData) => {
    if (regionData) {
      alert(`${regionData.region}: ${regionData.orders} pedidos - ${formatCurrency(regionData.revenue || 0)}`);
    }
  };

  if (loading) {
    return (
      <div className="card bg-transparent border-0">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3">Carregando relatórios AfterPay...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-transparent border-0">
        <div className="card-body text-center py-5">
          <Icon icon="mdi:alert-circle" className="text-danger" style={{ fontSize: '3rem' }} />
          <p className="text-danger mt-3">{error}</p>
          <Button variant="primary" onClick={loadReportsData}>
            <Icon icon="mdi:refresh" className="me-2" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Cards de Estatísticas Principais */}
      <div className="row g-3 top-cards" style={{ marginBottom: '24px' }}>
        <div className="col-md-3">
          <StatCard
            title="Vendas de Hoje"
            value={stats.salesToday.toLocaleString('pt-BR')}
            subtitle="Pedidos de hoje"
            variant="blue"
            icon="mdi:calendar-today"
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Vendas na Semana"
            value={stats.salesWeek.toLocaleString('pt-BR')}
            subtitle="Pedidos desta semana"
            variant="purple"
            icon="mdi:calendar-week"
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Vendas no Mês"
            value={stats.salesMonth.toLocaleString('pt-BR')}
            subtitle="Pedidos deste mês"
            variant="green"
            icon="mdi:calendar-month"
          />
        </div>
        <div className="col-md-3">
          <StatCard
            title="Taxa de Entrega"
            value={`${stats.deliveryRate}%`}
            subtitle="Pedidos entregues"
            variant="orange"
            icon="mdi:truck-check"
          />
        </div>
      </div>

      {/* Controles e Filtros */}
      <div className="card border-0 shadow-sm" style={{ marginBottom: '24px' }}>
        <div className="card-header bg-white border-bottom-0 py-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h6 className="card-title mb-1 d-flex align-items-center">
                <Icon icon="mdi:filter-variant" className="me-2 text-primary" />
                Filtros
              </h6>
            </div>
            <div className="col-md-6 text-end">
              <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                <select
                className="form-select flex-grow-1 flex-lg-grow-0 "
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  size="sm"
                  style={{ 
                    paddingRight: "32px",
                    textAlign: "left",
                    minWidth: '220px',
                    width: 'auto'
                   }}
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="15">Últimos 15 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="60">Últimos 60 dias</option>
                  <option value="90">Últimos 90 dias</option>
                </select>
                <select
                className='form-select'
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  size="sm"
                  style={{ 
                    width: 'auto',
                    textAlign: "left",
                    minWidth: '120px',
                    paddingRight: "32px"
                   }}
                >
                  <option value="all">Todas as Regiões</option>
                  <option value="BR-SP">São Paulo</option>
                  <option value="BR-RJ">Rio de Janeiro</option>
                  <option value="BR-MG">Minas Gerais</option>
                </select>
                <Button variant="success" size="sm" onClick={handleExportExcel}>
                  <Icon icon="mdi:file-excel" className="me-1" /> Excel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        {/* Gráfico de Linha - Evolução Mensal */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-bottom-0 pb-2">
              <h6 className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600' }}>
                <Icon icon="mdi:chart-line" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                Evolução Mensal de Pedidos
              </h6>
            </div>
            <div className="card-body pt-2">
              <div style={{ height: '250px' }}>
                <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de Pizza - Status dos Pedidos */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-bottom-0 pb-2">
              <h6 className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600' }}>
                <Icon icon="mdi:chart-pie" className="me-2 text-info" style={{ fontSize: '16px' }} />
                Status dos Pedidos
              </h6>
            </div>
                         <div className="card-body pt-2">
               <div style={{ height: '300px', width: '100%', position: 'relative' }}>
                 <Doughnut 
                   data={donutChartData} 
                   options={{ 
                     responsive: true, 
                     maintainAspectRatio: false,
                     plugins: {
                       legend: {
                         position: 'bottom',
                         labels: {
                           usePointStyle: true,
                           padding: 20,
                           font: { size: 12 }
                         }
                       }
                     }
                   }} 
                 />
               </div>
             </div>
          </div>
        </div>
      </div>

      {/* Mapa do Brasil e Top 5 Estados */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        {/* Mapa do Brasil */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600' }}>
                  <Icon icon="mdi:map" className="me-2 text-primary" style={{ fontSize: '16px', minWidth: '16px', minHeight: '16px' }} />
                  Mapa de Vendas por Estado
                </h6>
                <small className="text-muted">Cores correspondem às faixas da legenda • Clique nos estados para detalhes</small>
              </div>
            </div>
            <div className="card-body p-3">
              <div style={{ height: '450px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BrazilMapModern data={mapData} onRegionClick={handleRegionClick} />
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Estados */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-bottom-0 pb-2">
              <h6 className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600' }}>
                <Icon icon="mdi:trophy-variant" className="me-2 text-warning" style={{ fontSize: '16px' }} />
                Top 5 Estados
              </h6>
            </div>
            <div className="card-body pt-2">
              {top5States.length > 0 ? (
                <>
                  <ListGroup variant="flush">
                    {top5States.map((state, index) => (
                      <ListGroup.Item 
                        key={index} 
                        className="d-flex justify-content-between gap-2 align-items-center px-0 py-2 flex-row"
                        style={{
                          backgroundColor: 'transparent',
                          borderColor: 'var(--bs-border-color)'
                        }}
                      >
                          <Badge 
                            bg="light" 
                            text="dark" 
                            className="me"
                            style={{
                              backgroundColor: 'var(--bs-light)',
                              color: 'var(--bs-dark)'
                            }}
                          >
                            {index + 1}
                          </Badge>
                        <div className="d-flex align-items-start flex-column justify-content-between flex-md-row w-100 flex-lg-column">
                          <span className="fw-semibold text-muted text-truncate">
                            {state.region}
                          </span>
                          <div className='d-flex w-30 justify-content-between'>
                              <Badge bg="primary" className="me-2">{state.orders} Pedidos</Badge>
                              <span 
                                className="text-muted"
                                style={{ color: 'var(--bs-secondary-color)' }}
                                >
                                {formatCurrency(state.revenue || 0)}
                              </span>
                            </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  <div style={{ height: '150px', marginTop: '1rem', }} >
                    <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <Icon icon="mdi:chart-bar" className="text-muted mb-2" style={{ fontSize: '48px' }} />
                  <p className="text-muted mb-0">Nenhum dado de estados disponível</p>
                  <small className="text-muted">Os dados aparecerão quando houver vendas AfterPay</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AfterPayReportsLayer;
